// API client for the exit clearance workflow (/exitapp/ on the Django backend).
// Kept separate from the shared Redux saga files; uses the logged-in admin token.
import axios from "axios";
import { toast } from "react-toastify";
import { baseurlforexport } from "../../../text/apidata";
import { store } from "../../../Redux/Store";

const client = axios.create({ baseURL: `${baseurlforexport}/exitapp/` });

client.interceptors.request.use((config) => {
  const token = store.getState()?.users?.UserData?.session?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwraps the project's response envelope: { data, total_count, details, status: { message } }.
const unwrap = (res) => ({ data: res.data?.data, total: res.data?.total_count, message: res.data?.status?.message });

export class ExitApiError extends Error {
  constructor(message, details, code) {
    super(message);
    this.details = details || {};
    this.code = code;
  }
}

// No login: the candidate's rejoin form (CR-2), where the link token in the URL is the credential.
const publicClient = axios.create({ baseURL: `${baseurlforexport}/exitapp/` });

async function call(method, url, { params, data, quiet, http = client } = {}) {
  try {
    return unwrap(await http.request({ method, url, params, data }));
  } catch (err) {
    const body = err.response?.data || {};
    const message = body.status?.message || body.message || "Could not reach the server. Check your connection and try again.";
    if (err.response?.status === 401) toast.error("Your session has expired. Please log in again.");
    else if (!quiet) toast.error(message);
    throw new ExitApiError(message, body.details, err.response?.status);
  }
}

export const exitApi = {
  listCases: (params) => call("get", "cases/", { params }),
  getCase: (id) => call("get", `cases/${id}/`),
  createCase: (data) => call("post", "cases/", { data, quiet: true }),
  submitStage: (id, stage, values) => call("post", `cases/${id}/submit/`, { data: { stage, values }, quiet: true }),
  saveDraft: (id, stage, values) => call("post", `cases/${id}/draft/`, { data: { stage, values } }),
  cancelCase: (id, reason) => call("post", `cases/${id}/cancel/`, { data: { reason } }),
  saveLwd: (id, dateLeaving) => call("post", `cases/${id}/lwd/`, { data: { date_leaving: dateLeaving }, quiet: true }),
  saveEmployeeDetails: (id, data) => call("post", `cases/${id}/employee-details/`, { data, quiet: true }),
  bulkSubmit: (stage, caseIds) => call("post", "cases/bulk-submit/", { data: { stage, case_ids: caseIds } }),
  bulkInitiate: (file) => {
    const data = new FormData();
    data.append("file", file);
    return call("post", "cases/bulk-initiate/", { data });
  },
  bulkInitiateHistory: (params) => call("get", "cases/bulk-initiate/", { params }),
  bulkInitiatePreview: (file) => {
    const data = new FormData();
    data.append("file", file);
    return call("post", "cases/bulk-initiate/preview/", { data });
  },
  // Downloads the backend's upload template (.xlsx or .csv).
  bulkInitiateTemplate: async (fileFormat = "xlsx") => {
    try {
      const res = await client.get("cases/bulk-initiate/template/", { params: { file_format: fileFormat }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `exit_bulk_initiate_template.${fileFormat}` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Could not download the template. Please try again.");
    }
  },
  // Rejoin link (CR-2). rejoinStatus is quiet: users who are not exit HR get 403 and simply see no button.
  rejoinStatus: (employeeIds) => call("get", "rejoin/status/", { params: { employee_ids: employeeIds.join(",") }, quiet: true }),
  rejoinDetail: (employeeId) => call("get", `rejoin/${employeeId}/`),
  // Popup dropdowns: clients, employee types/categories; designations of clientId when given.
  rejoinOptions: (clientId) => call("get", "rejoin/options/", { params: clientId ? { client_id: clientId } : {} }),
  rejoinSend: (employeeId, data) => call("post", `rejoin/${employeeId}/send/`, { data, quiet: true }),
  rejoinForm: (token) => call("get", `rejoin-form/${token}/`, { quiet: true, http: publicClient }),
  rejoinFormSubmit: (token, data) => call("post", `rejoin-form/${token}/`, { data, quiet: true, http: publicClient }),
  summary: () => call("get", "summary/"),
  searchEmployees: (search, page = 1, page_size = 10) => call("get", "employees/", { params: { search, page, page_size } }),
  organizations: () => call("get", "config/organizations/"),
  stages: (organization) => call("get", "config/stages/", { params: { organization } }),
  saveStages: (organization, stages) => call("put", "config/stages/", { params: { organization }, data: { stages } }),
  options: (organization) => call("get", "config/options/", { params: { organization } }),
  saveOptions: (organization, rows) => call("post", "config/options/", { data: { organization, rows } }),
  approvers: (organization) => call("get", "config/approvers/", { params: { organization } }),
  addApprover: (data) => call("post", "config/approvers/", { data }),
  removeApprover: (id) => call("delete", `config/approvers/${id}/`),
  searchUsers: (search) => call("get", "config/users/", { params: { search } }),
  denaveRms: () => call("get", "config/denave-rms/"),
};

export const STAGE_SHORT = { EMP: "EMP", RM: "RM", SDM: "SDM", ADMIN: "Admin", IT: "IT", FIN: "Finance", HR: "HR", PAYROLL: "Payroll" };

export const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("-") : "");
// Local calendar date (YYYY-MM-DD) `days` away from today; avoids the UTC off-by-one of toISOString.
export const dateFromToday = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const fmtTs = (t) =>
  t ? new Date(t).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
export const fmtHours = (h) => (h == null ? "" : h < 24 ? `${Math.round(h)}h` : `${(h / 24).toFixed(1)}d`);

// Minimal CSV helpers for master uploads/downloads.
export function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  const [head = [], ...body] = rows;
  const keys = head.map((h) => h.trim().toLowerCase());
  return body.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? "").trim()])));
}

export function downloadCsv(filename, rows, columns) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [columns.map((c) => esc(c.label)).join(","), ...rows.map((r) => columns.map((c) => esc(c.get(r))).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
